import type { LocationSuggestion } from './types';

export interface SuggestionSection {
	key: string;
	title: string | undefined;
	items: LocationSuggestion[];
}

/**
 * Group autocomplete suggestions into backend-defined sections. When no
 * categories are returned, falls back to a single untitled group containing
 * all items. Any item whose `category` matches no returned type (or is
 * undefined) is collected into a trailing untitled group so nothing is
 * silently dropped from the list. Returns an empty array when there is
 * nothing to render.
 */
export function groupItemsBySection(
	items: LocationSuggestion[],
	categories: Array<{ type: string; title: string }>,
): SuggestionSection[] {
	if (!items.length) return [];
	if (!categories.length) {
		return [{ key: '_all', title: undefined, items }];
	}

	const knownTypes = new Set(categories.map(({ type }) => type));
	const sections: SuggestionSection[] = categories
		.map(({ type, title }) => ({
			key: type,
			title,
			items: items.filter((item) => item.category === type),
		}))
		.filter((section) => section.items.length);

	// Catch-all: surface items the backend didn't bucket into a known category
	// rather than hiding them (which would also suppress the zero-state).
	const uncategorized = items.filter(
		(item) => !item.category || !knownTypes.has(item.category),
	);
	if (uncategorized.length) {
		sections.push({
			key: '_uncategorized',
			title: undefined,
			items: uncategorized,
		});
	}

	return sections;
}
